import { ArrowLeft } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { MarketplaceChip } from "@/shared/components/MarketplaceChip";

import { baht } from "../lib/format";
import type { SplitGroup } from "../lib/types";

import { StatusBadge } from "./StatusBadge";

interface InfoRowProps {
  label: string;
  value: JSX.Element | string;
}

function InfoRow({ label, value }: InfoRowProps): JSX.Element {
  return (
    <div className="flex items-start gap-3 py-2 text-sm">
      <span className="w-44 shrink-0 text-[var(--text-subtle)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

interface SplitDetailProps {
  split: SplitGroup;
  onBack?: () => void;
}

export function SplitDetail({ split, onBack }: SplitDetailProps): JSX.Element {
  const { t } = useTranslation("orders");

  return (
    <div className="space-y-5 px-1 py-4">
      {/* Back link */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-subtle)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t("back_to_splits")}
        </button>
      )}

      {/* Summary card */}
      <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
        <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
          <div>
            <h3 className="font-semibold text-[var(--text-primary)]">{t("so_detail_title")}</h3>
            <span className="text-xs text-[var(--text-subtle)]">{split.sale}</span>
          </div>
          <div className="ml-auto">
            <MarketplaceChip marketplace={split.mkp} />
          </div>
        </div>
        <div className="divide-y divide-[var(--premium-border)] px-5">
          <InfoRow
            label={t("so_original")}
            value={<span className="font-mono">{split.sale}</span>}
          />
          <InfoRow label={t("so_customer")} value={split.cust} />
          <InfoRow
            label={t("th_total")}
            value={
              <b className="font-mono text-[var(--brand-700)]">
                {t("so_total_across", { total: baht(split.total), n: split.labels.length })}
              </b>
            }
          />
          <InfoRow label={t("th_status")} value={<StatusBadge status={split.status} />} />
        </div>
        <div className="px-5 pb-4 pt-3">
          <p className="rounded-lg bg-[var(--surface-sunken)] px-3 py-2.5 text-xs text-[var(--text-subtle)]">
            {t("so_purpose")}
          </p>
        </div>
      </div>

      {/* Labels card */}
      <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
        <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
          <h3 className="font-semibold text-[var(--text-primary)]">{t("th_labels")}</h3>
          <span className="text-xs text-[var(--text-subtle)]">{split.labels.length}</span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {split.labels.map((label) => (
              <div
                key={label.idx}
                className="rounded-lg border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]"
              >
                {/* Label card header */}
                <div className="flex items-center justify-between border-b border-[var(--premium-border)] px-4 py-2.5">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    {t("so_split_n", { n: label.idx, m: split.labels.length })}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] font-bold text-[var(--text-subtle)]">
                    {t(label.rule)}
                  </span>
                </div>

                {/* Label card body */}
                <div className="px-4 py-3">
                  <div className="mb-2 text-xs font-semibold text-[var(--text-subtle)]">
                    {t("so_label")}:{" "}
                    <span className="font-mono text-[var(--brand-700)]">{label.label}</span>
                  </div>
                  <div className="space-y-2">
                    {label.items.map((item) => (
                      <div
                        key={item.sino}
                        className="flex items-start justify-between gap-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-[var(--text-primary)]">{item.name}</div>
                          <div className="font-mono text-[11px] text-[var(--text-subtle)]">
                            {item.sino}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-sm tabular-nums text-[var(--text-secondary)]">
                          ×{item.q} · {baht(item.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Label card footer */}
                <div className="flex items-center justify-between border-t border-[var(--premium-border)] px-4 py-2.5">
                  <StatusBadge status={label.status} />
                  <span className="font-mono text-xs text-[var(--text-subtle)]">
                    {label.tracking ?? t("lbl_pending")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trace card */}
      <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
        <div className="border-b border-[var(--premium-border)] px-5 py-3">
          <h3 className="font-semibold text-[var(--text-primary)]">{t("trace_title")}</h3>
        </div>
        <div className="px-5 py-4">
          <ol className="space-y-3">
            {split.trace.map((entry, i) => {
              const actText =
                entry.act === "own"
                  ? t("trace_own")
                  : entry.act === "overflow"
                    ? t("trace_overflow")
                    : t("trace_group", { n: entry.n });

              return (
                <li key={`${entry.rule}-${entry.sku}-${i}`} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--brand-500)] text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="pt-0.5 text-sm">
                    <b className="text-[var(--text-primary)]">{t(entry.rule)}</b>
                    {" · "}
                    <span className="font-mono text-[var(--text-secondary)]">{entry.sku}</span>{" "}
                    <span className="text-[var(--text-subtle)]">{actText}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}
